import {
  ArchivedProperties,
  PropertiesChart,
  PropertyForm,
  PropertyList,
  PropertyTotals,
} from "@gladis/domain-properties/ui";
import { Button, Dialog, Panel } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/properties")({
  component: Properties,
});

/**
 * Properties.
 *
 * Composition only — the list and the form come from the properties package, which owns what a
 * property is. This route decides where they appear.
 *
 * Nothing here fetches the member list, and that absence is the difference from the Savings page. A
 * savings account can be owned by one member, so that route has to supply identity's answer; a
 * property has no owner within the household, so this one joins nothing.
 */
function Properties(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  return (
    <Page>
      {/*
       * ADDING A PROPERTY IS THIS PAGE'S PRIMARY ACTION, so it goes where `Page.Title` puts every page's:
       * the end of the title row. It used to live inside `PropertyList` — twice, once above the table and
       * once in its empty state — which put the page's most important control at whatever height that
       * section happened to start.
       *
       * `aria-haspopup="dialog"`, AND IT USED TO BE `aria-expanded`. The note here said the difference was
       * real: expenses and cards open modals, while this form opened inline above the list with no cancel
       * of its own, so the control TOGGLED and had to say so. The form is a modal now, like every other
       * one in the application, so the control announces a dialog and no longer toggles anything —
       * `Escape` and the dialog's own close are the way out.
       */}
      <Page.Title
        actions={
          <Button
            variant="primary"
            aria-haspopup="dialog"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("page.properties.addProperty")}
          </Button>
        }
      >
        {t("nav.properties")}
      </Page.Title>
      <Page.Body>
        <div className="col-span-full flex flex-col gap-6">
          {/*
           * THE ORDER IS THE ARGUMENT: what are we worth, how has it moved, which house is which.
           *
           * The totals answer the first question in three numbers and belong at the top. They used
           * to live inside `PropertyList`, which put them BELOW the chart the moment the chart moved
           * up — a summary underneath the graph it summarises is a summary nobody reads.
           */}
          <PropertyTotals />

          {/*
           * THE CHART COMES SECOND, and the order is the argument.
           *
           * It answers "how has it moved" in one glance; the table answers "which house is which"
           * and is where you go once you have a reason to. The list was above it because the list
           * was built first.
           */}
          <PropertiesChart />

          <Panel title={t("nav.properties")} description={t("page.properties.hint")}>
            <PropertyList
              onOpen={(propertyId: string) => {
                void navigate({ to: "/properties/$propertyId", params: { propertyId } });
              }}
            />
          </Panel>

          <ArchivedProperties />
        </div>
      </Page.Body>

      {/*
       * MOUNTED ONLY WHILE OPEN, the rule 016's E2E found the bug for: a dialog left mounted keeps
       * the `useState` from the first time it was opened, so a second property starts with the
       * first one's street still in the field.
       *
       * `wide`, because this form has eleven inputs and a two-column grid — the default width would
       * put the postcode under the street and make it read as a single column of unrelated boxes.
       */}
      {adding && (
        <Dialog
          open
          onClose={() => {
            setAdding(false);
          }}
          title={t("page.properties.addProperty")}
          description={t("page.properties.addHint")}
          size="wide"
        >
          <PropertyForm
            onCreated={() => {
              setAdding(false);
            }}
          />
        </Dialog>
      )}
    </Page>
  );
}
